import { InlineMath, BlockMath } from "react-katex";
import PairInput from "../../../../components/inputs/PairInput";
import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";
import { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import FractionInput from "../../../../components/inputs/FractionInput";

function getRandomInt(min, max, exclude = []) {
  let num;
  do {
    num = Math.floor(Math.random() * (max - min + 1)) + min;
  } while (exclude.includes(num));
  return num;
}

function gcd(a, b) {
  while (b !== 0) [a, b] = [b, a % b];
  return Math.abs(a);
}

function formatMixedFractionLatex(value) {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const intPart = Math.floor(abs);
  const frac = abs - intPart;

  if (frac === 0) return `${sign}${intPart}`;

  // Szukamy ułamka skracalnego z dokładnością do 1/100
  const maxDen = 10; // np. 10 → pozwala na 1/2, 1/3, ..., 9/10
  for (let d = 1; d <= maxDen; d++) {
    const n = Math.round(frac * d);
    if (Math.abs(n / d - frac) < 0.01) {
      const g = gcd(n, d);
      const num = n / g;
      const den = d / g;

      if (intPart === 0) return `${sign}\\dfrac{${num}}{${den}}`;
      return `${sign}${intPart}\\dfrac{${num}}{${den}}`;
    }
  }

  // fallback jeśli nie znaleziono ładnej postaci
  return `${sign}${abs.toFixed(2)}`;
}

function solveIntersection(a1, b1, a2, b2) {
  const x = (b2 - b1) / (a1 - a2);
  const y = a1 * x + b1;
  return [x, y];
}

/** ONLY FOR DISPLAY (żeby nie było 1x i -1x) */
function formatAxLatex(a) {
  if (a === 1) return "x";
  if (a === -1) return "-x";
  return `${a}x`;
}
function formatAxExpr(a) {
  if (a === 1) return "x";
  if (a === -1) return "-x";
  return `${a} * x`;
}

export function generateTask() {
  let a1, a2;
  do {
    a1 = getRandomInt(-5, 5, [0]);
    a2 = getRandomInt(-5, 5, [0, a1]);
  } while (a1 === a2);

  let b1, b2;
  do {
    b1 = getRandomInt(-5, 5, [0]);
    b2 = getRandomInt(-5, 5, [0]);
  } while (b1 === b2);

  const [x, y] = solveIntersection(a1, b1, a2, b2);

  // ✅ odrzucamy przypadki poza zakresem
  if (Math.abs(x) > 8 || Math.abs(y) > 8) {
    return generateTask(); // ponawiamy losowanie
  }

  return {
    type: "IntersectionAnalysisTask",
    data: { a1, b1, a2, b2 },
    answer: [x, y],
  };
}

export function renderTask({ data }) {
  const { a1, b1, a2, b2 } = data;

  const a1Str = a1 === 1 ? "x" : a1 === -1 ? "-x" : `${a1}x`;
  const b1Str = b1 === 0 ? "" : b1 > 0 ? ` + ${b1}` : ` - ${Math.abs(b1)}`;
  const eq1 = `y = ${a1Str}${b1Str}`;

  const a2Str = a2 === 1 ? "x" : a2 === -1 ? "-x" : `${a2}x`;
  const b2Str = b2 === 0 ? "" : b2 > 0 ? ` + ${b2}` : ` - ${Math.abs(b2)}`;
  const eq2 = `y = ${a2Str}${b2Str}`;

  return (
    <div className="space-y-2">
      <p>Podaj współrzędne punktu przecięcia prostych:</p>
      <BlockMath math={eq1} />
      <BlockMath math={eq2} />
    </div>
  );
}

/** ✅ Input: 2× FractionInput (x i y osobno) */
export function renderInput(_, userAnswer, onChange, disabled) {
  const ua = userAnswer ?? {};
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span><InlineMath math={'x ='} /></span>
        <FractionInput
          value={ua?.x || ""}
          onChange={(val) => onChange({ ...ua, x: val })}
          disabled={disabled}
          label="x"
        />
      </div>
      <div className="flex items-center gap-2">
        <span><InlineMath math={'y ='} /></span>
        <FractionInput
          value={ua?.y || ""}
          onChange={(val) => onChange({ ...ua, y: val })}
          disabled={disabled}
          label="y"
        />
      </div>
    </div>
  );
}

export function checkAnswer(correctAnswer, userAnswer) {
  console.log("DEBUG correctAnswer", correctAnswer);
  console.log("DEBUG userAnswer", userAnswer);

  // Obsługa obiektu z answer
  const answerArray = Array.isArray(correctAnswer)
    ? correctAnswer
    : Array.isArray(correctAnswer?.answer)
    ? correctAnswer.answer
    : null;

  if (
    !answerArray || !Array.isArray(answerArray) || answerArray.length !== 2 ||
    !userAnswer || typeof userAnswer !== "object"
  ) {
    console.log("❌ Błąd: brak danych lub zły format wejścia");
    return false;
  }

  const ux = userAnswer.x?.parsed;
  const uy = userAnswer.y?.parsed;

  console.log("DEBUG ux =", ux, "uy =", uy);

  if (typeof ux !== "number" || typeof uy !== "number") {
    console.log("❌ Błąd: przynajmniej jedno z parsed nie jest liczbą");
    return false;
  }

  const [cx, cy] = answerArray;

  // 🔧 dokładność do dwóch miejsc po przecinku
  const isClose = (a, b) => Math.abs(a - b) < 0.005;

  const dx = Math.abs(ux - cx);
  const dy = Math.abs(uy - cy);

  console.log(`COMPARE x: ${ux} vs ${cx} → Δ = ${dx}`);
  console.log(`COMPARE y: ${uy} vs ${cy} → Δ = ${dy}`);

  const result = isClose(ux, cx) && isClose(uy, cy);
  console.log("✅ Result:", result);

  return result;
}

export function renderAnswer({ answer }) {
  const [x, y] = answer;

  const xLatex = formatMixedFractionLatex(x);
  const yLatex = formatMixedFractionLatex(y);

  return (
    <div style={{ marginTop: "1rem" }}>
      <InlineMath math={`P = \\left(${xLatex},\\ ${yLatex}\\right)`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer !== "object" ||
    !userAnswer.x?.raw ||
    !userAnswer.y?.raw
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const xLatex = formatLatexPreview(userAnswer.x.raw, "x").split("=")[1];
  const yLatex = formatLatexPreview(userAnswer.y.raw, "y").split("=")[1];

  return (
    <InlineMath math={`P = \\left(${xLatex},\\ ${yLatex}\\right)`} />
  );
}

export function renderExplanation({ data, answer }) {
  const { a1, b1, a2, b2 } = data;

  if (!answer || !Array.isArray(answer) || answer.length !== 2) {
    return <div style={{ color: "red" }}>Błąd: brak lub nieprawidłowy format odpowiedzi.</div>;
  }

  const [x, y] = answer;

  const xLatex = formatMixedFractionLatex(x);
  const yLatex = formatMixedFractionLatex(y);

  const b1Part = b1 >= 0 ? "+ " + b1 : "- " + Math.abs(b1);
  const b2Part = b2 >= 0 ? "+ " + b2 : "- " + Math.abs(b2);

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1:</strong> Mamy układ równań:
        <BlockMath math={`\\begin{cases}
y = ${formatAxLatex(a1)} ${b1Part} \\\\
y = ${formatAxLatex(a2)} ${b2Part}
\\end{cases}`} />
      </div>

      <div>
        <strong>Krok 2:</strong> Przyrównujemy prawe strony równań:
        <BlockMath math={`${formatAxLatex(a1)} ${b1Part} = ${formatAxLatex(a2)} ${b2Part}`} />
      </div>

      <div>
        <strong>Krok 3:</strong> Przenosimy wszystkie wyrazy z <InlineMath math="x" /> na jedną stronę, a liczby na drugą:
        <BlockMath math={`${a1}x - ${a2}x = ${b2} - ${b1}`} />
        <BlockMath math={`${a1 - a2}x = ${b2 - b1}`} />
        <BlockMath math={`x = \\frac{${b2 - b1}}{${a1 - a2}} = ${xLatex}`} />
      </div>

      <div>
        <strong>Krok 4:</strong> Podstawiamy <InlineMath math={`x = ${xLatex}`} /> do pierwszego równania:
        <BlockMath math={`y = ${a1} \\cdot ${xLatex} ${b1Part} = ${yLatex}`} />
      </div>

      <div style={{ marginBottom: "1rem" }}>
        <strong>Odpowiedź:</strong> <BlockMath math={`P = \\left(${xLatex},\\ ${yLatex}\\right)`} />
      </div>

      <div>
        <strong>Przedstawienie graficzne:</strong>
        <div style={{ display: "flex", alignItems: "center", gap: "2rem" }}>
          <GraphViewer
            points={{
              "": { x, y, label: "" }
            }}
            equations={[
              {
                expr: `y = ${formatAxExpr(a1)} ${b1Part}`,
                color: "purple",
                label: "prosta 1"
              },
              {
                expr: `y = ${formatAxExpr(a2)} ${b2Part}`,
                color: "green",
                label: "prosta 2"
              }
            ]}
            xRange={[-8, 8]}
            yRange={[-8, 8]}
            showGrid
            width={500}
            height={500}
          />

          {/* ✅ podpis obok wykresu zostaje — tylko bez 1x / -1x */}
          <div style={{ fontSize: "1rem", lineHeight: "1.6" }}>
            <div style={{ color: "#c700f2" }}>
              <InlineMath math={`y = ${formatAxLatex(a1)} ${b1Part}`} />
            </div>
            <div style={{ color: "#00c466" }}>
              <InlineMath math={`y = ${formatAxLatex(a2)} ${b2Part}`} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
